import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { tv } from "tailwind-variants";

import { ROUTES } from "~/router";
import { usePermissions } from "~/screens/Admins/hooks/usePermissions";
import { Navbar } from "~/sections";
import { AdminMobileNav } from "./admin-mobile-nav";

type MainLayoutProps = {
  children: React.ReactNode;
};

const navButton = tv({
  base: "text-base font-normal transition-colors border-b-2 hover:text-indigo",
  variants: {
    isActive: {
      true: "border-indigo text-indigo",
      false: "border-transparent text-gray-500",
    },
  },
});

export const MainLayout = ({ children }: MainLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    canViewProviderSchedule,
    canViewBanners,
    canManageInsuranceProviders,
    canViewUserEvents,
  } = usePermissions();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isProviderDashboard = location.pathname.includes(
    ROUTES.adminProviderDashboard,
  );
  const isBannerManagement = location.pathname.includes(
    ROUTES.adminBannerManagement,
  );
  const isInsuranceProviders = location.pathname.includes(
    ROUTES.adminInsuranceProviders,
  );
  const isUserEvents = location.pathname.includes(ROUTES.adminUserEvents);

  const navItems = [
    {
      label: "User Dashboard",
      route: ROUTES.dashboard,
      isActive:
        !isProviderDashboard &&
        !isBannerManagement &&
        !isInsuranceProviders &&
        !isUserEvents,
      show: true,
    },
    {
      label: "User Events",
      route: ROUTES.adminUserEvents,
      isActive: isUserEvents,
      show: canViewUserEvents,
    },
    {
      label: "Clinician Dashboard",
      route: ROUTES.adminProviderDashboard,
      isActive: isProviderDashboard,
      show: canViewProviderSchedule,
    },
    {
      label: "Banner Management",
      route: ROUTES.adminBannerManagement,
      isActive: isBannerManagement,
      show: canViewBanners,
    },
    {
      label: "Insurance Providers",
      route: ROUTES.adminInsuranceProviders,
      isActive: isInsuranceProviders,
      show: canManageInsuranceProviders,
    },
  ];

  return (
    <section>
      <AdminMobileNav
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <Navbar canLogout setSidebarOpen={setSidebarOpen}>
        <div className="hidden gap-x-6 lg:flex">
          {navItems
            .filter((item) => item.show)
            .map((item) => (
              <button
                key={item.label}
                onClick={() => navigate(item.route)}
                className={navButton({ isActive: item.isActive })}
              >
                {item.label}
              </button>
            ))}
        </div>
      </Navbar>
      <main className="py-6 md:py-[60px]">
        <section className="px-4 sm:px-6 lg:px-[60px]">{children}</section>
      </main>
    </section>
  );
};
